import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { Preferences } from '@capacitor/preferences';

import { fetchFeedFilter, saveFeedFilter } from '@/api/feedFilter';

/**
 * feed-genre-filter — the user's music-style filter.
 *
 * ONE global selection, shared by every feed (Racore Official and each circle).
 * A per-circle map was rejected: "my filter" is an attribute of the person, like
 * their language, and the hidden-count banner already covers the case a circle
 * gets emptied by a filter set elsewhere.
 *
 * `toggle` APPLIES (the feed refilters instantly, the user sees the effect).
 * `commit` PUSHES to the account, and is called when the filter sheet CLOSES —
 * the sheet closing is the user's decision signal, a gesture they already make,
 * so there is no "Apply" button and no debounce timer, and one PUT per session.
 *
 * filter-sheet-cleanup — the recent-filters history is gone (G, 2026-10-12),
 * from memory, from the device (persist v1 migrates it away) and from the
 * account. Clearing the selection can no longer be undone.
 *
 * Persisted locally FIRST so the first paint is already filtered (no unfiltered
 * flash), then pushed to the backend on commit so it follows the account rather
 * than the install.
 */

const capacitorStorage: StateStorage = {
  getItem: async (name) => (await Preferences.get({ key: name })).value,
  setItem: async (name, value) => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name) => {
    await Preferences.remove({ key: name });
  },
};

interface FeedFilterState {
  styles: string[];
  toggle: (style: string) => void;
  clear: () => void;
  /** Called when the filter sheet closes: pushes the selection to the account. */
  commit: () => void;
  /** Seeds from the account at login. Never blocks the first paint. */
  hydrateFromServer: () => Promise<void>;
}

export const useFeedFilter = create<FeedFilterState>()(
  persist(
    (set, get) => ({
      styles: [],

      toggle: (style) =>
        set((s) => ({
          styles: s.styles.includes(style)
            ? s.styles.filter((v) => v !== style)
            : [...s.styles, style],
        })),

      clear: () => set({ styles: [] }),

      commit: () => {
        // One PUT per filtering session, fired on the decision signal. Never
        // awaited: the selection is already applied and persisted locally.
        void saveFeedFilter(get().styles);
      },

      hydrateFromServer: async () => {
        const remote = await fetchFeedFilter();
        // `null` = offline or contract not deployed: keep what the device has.
        if (remote) set({ styles: remote.styles });
      },
    }),
    {
      name: 'racore-feed-filter',
      storage: createJSONStorage(() => capacitorStorage),
      // Only the data persists; the actions are rebuilt on each boot.
      partialize: (s) => ({ styles: s.styles }),
      // filter-sheet-cleanup — v0 also stored `history`. `persist` merges back
      // whatever it reads, so without this the old history would be re-injected
      // into the state on every boot.
      version: 1,
      migrate: (persisted) => ({
        styles: Array.isArray((persisted as { styles?: unknown })?.styles)
          ? (persisted as { styles: string[] }).styles
          : [],
      }),
    },
  ),
);
